import React from 'react'
import PropTypes from 'prop-types'
import { css } from '@emotion/core'
import { trackLink } from '@fs/zion-analytics'
import { ArrowCaret } from '@fs/zion-icon'
import { useContainerWidth, colors, IconButton, Separator, Row } from '@fs/zion-ui'
import { Link, useLocation } from '@fs/zion-router'
import usePrototypeCards from './getting-started-cards/useCards'

const navCss = css`
  text-transform: uppercase;
  color: ${colors.blue50};
`
const leftTextCss = css`
  line-height: 18px;
  min-width: 45%;
`

const rightTextCss = css`
  line-height: 18px;
  text-align: end;
  min-width: 45%;
`

const leftTextMdCss = css`
  padding-inline-start: 1em;
`

const rightTextMdCss = css`
  padding-inline-end: 1em;
  text-align: end;
`

export default function NavArrows() {
  const atWidth = useContainerWidth()
  const [leftCard, rightCard] = usePrevNextCards()
  // Pulling out the excludeLangs array that may be there to prevent it from going onto the links
  const { title: leftText, excludeLangs: excludeLeft, ...leftProps } = leftCard || {}
  const { title: rightText, excludeLangs: excludeRight, ...rightProps } = rightCard || {}
  return (
    <div>
      <Separator size="xl" />

      {atWidth({
        default: (
          <div>
            <Row css={navCss}>
              <div css={leftTextCss}>
                <NavArrowLink text={leftText} {...leftProps} />
              </div>
              <div css={rightTextCss}>
                <NavArrowLink text={rightText} {...rightProps} />
              </div>
            </Row>

            <Separator size="xxs" />

            <Row css={navCss}>
              {leftCard ? (
                <NavArrowIconButton growX iconDirection="backward" {...leftProps} title={leftText} />
              ) : (
                <div growX css={leftTextCss} />
              )}
              {rightCard ? (
                <NavArrowIconButton iconDirection="forward" {...rightProps} title={rightText} />
              ) : (
                <div growX css={rightTextCss} />
              )}
            </Row>
          </div>
        ),
        md: (
          <Row css={navCss} alignX="end">
            {leftCard ? (
              <Row growX>
                <NavArrowIconButton iconDirection="backward" {...leftProps} title={leftText} />
                <Row alignY="middle" css={leftTextMdCss}>
                  <NavArrowLink text={leftText} {...leftProps} />
                </Row>
              </Row>
            ) : (
              <Row />
            )}
            {rightCard ? (
              <Row>
                <Row alignY="middle" css={rightTextMdCss}>
                  <NavArrowLink text={rightText} {...rightProps} />
                </Row>
                <NavArrowIconButton iconDirection="forward" {...rightProps} title={rightText} />
              </Row>
            ) : (
              <Row />
            )}
          </Row>
        ),
      })}

      <Separator size="xl" />
    </div>
  )
}

NavArrows.propTypes = {
  insetInlineStart: PropTypes.shape({
    to: PropTypes.string,
    text: PropTypes.string,
    linkName: PropTypes.string,
  }),
  insetInlineEnd: PropTypes.shape({
    to: PropTypes.string,
    text: PropTypes.string,
    linkName: PropTypes.string,
  }),
}

export function usePrevNextCards() {
  const { pathname } = useLocation()

  const prototypeCards = usePrototypeCards()

  const cards = prototypeCards.filter((card) => card.cardType !== 'help')
  const currentIndex = cards.findIndex((card) => card.to === pathname)
  let prevCard
  let nextCard
  // Weird things happen when currentIndex is -1 (card wasn't found, e.g. on a page not supported in the current language), so this check prevents that
  if (currentIndex >= 0) {
    prevCard = cards[currentIndex - 1]
    if (prevCard) {
      prevCard['data-testid'] = 'previousPageLink'
    }
    nextCard = cards[currentIndex + 1]
    if (nextCard) {
      nextCard['data-testid'] = 'nextPageLink'
    }
  }
  return [prevCard, nextCard]
}

function NavArrowLink({ text, to, ...props }) {
  if (to) {
    return (
      <Link wayfinding to={to} linkName={props.linkName} data-testid={props['data-testid']}>
        {text}
      </Link>
    )
  }

  return <div>{text}</div>
}

function NavArrowIconButton({ linkName, iconDirection, to, title }) {
  return (
    <IconButton
      title={title}
      size="lg"
      Icon={ArrowCaret}
      iconDirection={iconDirection}
      emphasis="high"
      data-link-name={linkName}
      onClick={trackLink}
      to={to}
    />
  )
}
